import React, { useState, useCallback, useEffect } from 'react';
import { FeatureInfoButton } from '../FeatureDocumentation';
import {
  MapContainer,
  TileLayer,
  Circle,
  CircleMarker,
  Popup,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  rebuildPatternOfLife,
  checkDeviation,
} from '../../../services/patternOfLife.service';
import {
  getSmartGeofences,
  checkGeofenceCompliance,
  getGeofenceTypeConfig,
  getComplianceStatusConfig,
  formatActiveHours,
} from '../../../services/smartGeofence.service';
import AddressDisplay from '../../AddressDisplay';

// Fix Leaflet icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// ── Constants ──────────────────────────────────────────────────────────────────

const LOCATION_TYPE_CONFIG = {
  home: { icon: '\uD83C\uDFE0', color: '#22C55E', label: 'Home' },
  work: { icon: '\uD83C\uDFE2', color: '#3B82F6', label: 'Work' },
  frequent: { icon: '\uD83D\uDCCD', color: '#F59E0B', label: 'Frequent' },
  unknown: { icon: '\u2753', color: '#6B7280', label: 'Unknown' },
};

const ALERT_LEVEL_CONFIG = {
  none: { color: '#22C55E', bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'None' },
  low: { color: '#F59E0B', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Low' },
  medium: { color: '#F97316', bgColor: 'bg-orange-100', textColor: 'text-orange-800', label: 'Medium' },
  high: { color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'High' },
};

const TIME_PERIODS = ['morning', 'midday', 'afternoon', 'evening', 'night'];
const TIME_PERIOD_LABELS = {
  morning: '6am-12pm',
  midday: '12pm-2pm',
  afternoon: '2pm-6pm',
  evening: '6pm-10pm',
  night: '10pm-6am',
};
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ── Map helper components ──────────────────────────────────────────────────────

const ZoomToLocation = ({ location }) => {
  const map = useMap();
  React.useEffect(() => {
    if (location) {
      map.setView([location.centerLat, location.centerLon], 15);
    }
  }, [location, map]);
  return null;
};

const FitBounds = ({ locations }) => {
  const map = useMap();
  React.useEffect(() => {
    if (locations && locations.length > 0) {
      const bounds = locations.map((loc) => [loc.centerLat, loc.centerLon]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  return null;
};

// ── Utility functions ──────────────────────────────────────────────────────────

const formatDwellTime = (minutes) => {
  if (!minutes) return 'N/A';
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getHeatmapColor = (value) => {
  if (value === undefined || value === null) return 'bg-gray-100';
  if (value >= 0.8) return 'bg-green-500';
  if (value >= 0.6) return 'bg-green-400';
  if (value >= 0.4) return 'bg-green-300';
  if (value >= 0.2) return 'bg-green-200';
  if (value > 0) return 'bg-green-100';
  return 'bg-gray-100';
};

// ── Component ──────────────────────────────────────────────────────────────────

const PolLocationTab = ({ personId }) => {
  // POL Model state
  const [polModel, setPolModel] = useState(null);
  const [deviation, setDeviation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Smart Geofencing state
  const [smartGeofences, setSmartGeofences] = useState(null);
  const [complianceResult, setComplianceResult] = useState(null);
  const [isLoadingGeofences, setIsLoadingGeofences] = useState(false);

  // Unified Location Check state
  const [checkLat, setCheckLat] = useState('');
  const [checkLon, setCheckLon] = useState('');
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);

  // Local error
  const [error, setError] = useState(null);

  // ── Utility ──────────────────────────────────────────────────────────────────

  const getMapCenter = () => {
    if (!polModel?.significantLocations?.length) return [33.4484, -112.074]; // Default Phoenix
    const locs = polModel.significantLocations;
    const lat = locs.reduce((sum, l) => sum + l.centerLat, 0) / locs.length;
    const lon = locs.reduce((sum, l) => sum + l.centerLon, 0) / locs.length;
    return [lat, lon];
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleLoadModel = useCallback(async () => {
    if (!personId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsLoading(true);
    setError(null);
    setPolModel(null);
    setDeviation(null);

    try {
      // Always rebuild to ensure fresh data and get drift metrics
      const data = await rebuildPatternOfLife(personId);
      if (data) {
        setPolModel(data);
      } else {
        setError('No location data found. Ensure this person has GPS pings.');
      }
    } catch (err) {
      setError('Failed to load Pattern of Life model');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [personId]);

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
  };

  const handleGetSmartGeofences = useCallback(async () => {
    if (!personId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsLoadingGeofences(true);
    setError(null);
    setSmartGeofences(null);

    try {
      const result = await getSmartGeofences(parseInt(personId, 10));
      if (result) {
        setSmartGeofences(result);
      } else {
        setError('No smart geofences found. Build a POL model first.');
      }
    } catch (err) {
      setError('Failed to get smart geofences: ' + (err.message || 'Unknown error'));
      console.error(err);
    } finally {
      setIsLoadingGeofences(false);
    }
  }, [personId]);

  const handleCheckLocation = useCallback(async (overrideLat, overrideLon) => {
    if (!personId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsCheckingLocation(true);
    setError(null);
    setDeviation(null);
    setComplianceResult(null);

    const lat = overrideLat != null ? overrideLat : (checkLat ? parseFloat(checkLat) : undefined);
    const lon = overrideLon != null ? overrideLon : (checkLon ? parseFloat(checkLon) : undefined);

    // Run both checks in parallel
    const [devResult, compResult] = await Promise.allSettled([
      checkDeviation(personId, lat, lon),
      (lat != null && lon != null)
        ? checkGeofenceCompliance(parseInt(personId, 10), lat, lon)
        : Promise.resolve(null),
    ]);

    if (devResult.status === 'fulfilled' && devResult.value) {
      setDeviation(devResult.value);
    }
    if (compResult.status === 'fulfilled' && compResult.value) {
      setComplianceResult(compResult.value);
    }

    if (devResult.status === 'rejected' && compResult.status === 'rejected') {
      setError('Location check failed. Ensure POL model exists.');
    }

    setIsCheckingLocation(false);
  }, [personId, checkLat, checkLon]);

  // Auto-load POL model and geofences when personId is set
  useEffect(() => {
    if (personId && !polModel && !isLoading) {
      handleLoadModel();
    }
    if (personId && !smartGeofences && !isLoadingGeofences) {
      handleGetSmartGeofences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* ================================================================== */}
      {/*  SECTION 1 — Known Locations                                       */}
      {/* ================================================================== */}
      <div id="known-locations">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-xl font-bold">Known Locations</h2>
          <FeatureInfoButton feature="pol" label="Docs" />
        </div>
        {/* Admin bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={handleLoadModel}
              disabled={isLoading}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Loading...
                </span>
              ) : (
                'Load POL Model'
              )}
            </button>
            <button
              onClick={handleGetSmartGeofences}
              disabled={!personId || isLoadingGeofences}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              {isLoadingGeofences ? 'Loading...' : 'Load Smart Geofences'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Rebuilds the model from all GPS data and compares to the previous build to detect pattern changes.
          </p>
        </div>

        {/* Loading State when no data */}
        {isLoading && !polModel && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <span className="ml-4 text-gray-500">
              Building Pattern of Life model...
            </span>
          </div>
        )}

        {/* Empty State */}
        {!polModel && !isLoading && !error && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-6xl mb-4">{'\uD83D\uDCCD'}</div>
            <h3 className="text-xl font-semibold mb-2">Pattern of Life Testing</h3>
            <p className="text-gray-500 mb-4">
              Enter a Person ID above and click "Load POL Model" to view their location patterns,
              or "Rebuild Model" to reprocess from GPS data.
            </p>
          </div>
        )}

        {/* Main Content Grid */}
        {polModel && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Results Panel */}
            <div className="lg:col-span-1 space-y-6">
              {/* Data Summary */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3">Data Summary</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-2 rounded">
                    <div className="text-gray-500">Person ID</div>
                    <div className="font-semibold">{polModel.personId || personId}</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <div className="text-gray-500">Locations</div>
                    <div className="font-semibold">{polModel.significantLocations?.length || 0}</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <div className="text-gray-500">GPS Pings</div>
                    <div className="font-semibold">{polModel.recordCount || 'N/A'}</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <div className="text-gray-500">Model Updated</div>
                    <div className="font-semibold text-xs">{formatDate(polModel.modelTimestamp || polModel.model_timestamp)}</div>
                  </div>
                </div>
              </div>

              {/* Pattern Stability */}
              {polModel.patternDrift && (
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-semibold mb-3">Pattern Stability</h3>
                  {/* Stability Score */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600">Stability Score</span>
                      <span className={`text-lg font-bold ${
                        polModel.patternDrift.stabilityScore >= 80 ? 'text-green-600' :
                        polModel.patternDrift.stabilityScore >= 50 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {polModel.patternDrift.stabilityScore}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          polModel.patternDrift.stabilityScore >= 80 ? 'bg-green-500' :
                          polModel.patternDrift.stabilityScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${polModel.patternDrift.stabilityScore}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {polModel.patternDrift.stabilityScore >= 80 ? 'Consistent routine, no significant changes' :
                       polModel.patternDrift.stabilityScore >= 50 ? 'Some behavioral changes detected' :
                       'Significant pattern shift detected'}
                    </p>
                  </div>
                  {/* Drift Details */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {polModel.patternDrift.homeDriftMeters != null && (
                      <div className="bg-gray-50 p-2 rounded">
                        <div className="text-gray-500">Home Drift</div>
                        <div className={`font-semibold ${polModel.patternDrift.homeDriftMeters > 200 ? 'text-red-600' : 'text-green-600'}`}>
                          {Math.round(polModel.patternDrift.homeDriftMeters)}m
                        </div>
                      </div>
                    )}
                    {polModel.patternDrift.workDriftMeters != null && (
                      <div className="bg-gray-50 p-2 rounded">
                        <div className="text-gray-500">Work Drift</div>
                        <div className={`font-semibold ${polModel.patternDrift.workDriftMeters > 200 ? 'text-red-600' : 'text-green-600'}`}>
                          {Math.round(polModel.patternDrift.workDriftMeters)}m
                        </div>
                      </div>
                    )}
                    <div className="bg-gray-50 p-2 rounded">
                      <div className="text-gray-500">Routine Shift</div>
                      <div className={`font-semibold ${polModel.patternDrift.routineShiftPct > 10 ? 'text-yellow-600' : 'text-green-600'}`}>
                        {polModel.patternDrift.routineShiftPct}%
                      </div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded">
                      <div className="text-gray-500">Locations</div>
                      <div className="font-semibold">
                        {polModel.patternDrift.locationsOldCount} &rarr; {polModel.patternDrift.locationsNewCount}
                      </div>
                    </div>
                  </div>
                  {/* Location changes */}
                  {polModel.patternDrift.locationChanges?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {polModel.patternDrift.locationChanges.map((change, idx) => (
                        <div key={idx} className="text-xs px-2 py-1 bg-yellow-50 border border-yellow-200 rounded flex items-center gap-1">
                          <span className="text-yellow-600 font-medium capitalize">{change.type}</span>
                          <span className="text-gray-500">
                            {change.change === 'moved'
                              ? `moved ${Math.round(change.distanceMeters)}m`
                              : `${change.oldCount} \u2192 ${change.newCount} locations`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {polModel.patternDrift.previousModelTimestamp && (
                    <p className="text-xs text-gray-400 mt-2">
                      Compared to model from {formatDate(polModel.patternDrift.previousModelTimestamp)}
                    </p>
                  )}
                </div>
              )}

              {/* Known Locations List */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3">Known Locations</h3>
                {polModel.significantLocations?.length > 0 ? (
                  <div className="space-y-2">
                    {polModel.significantLocations.map((location, idx) => {
                      const config = LOCATION_TYPE_CONFIG[location.locationType] || LOCATION_TYPE_CONFIG.unknown;
                      const isSelected = selectedLocation?.locationId === location.locationId;

                      return (
                        <div
                          key={location.locationId || idx}
                          onClick={() => handleLocationClick(location)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{config.icon}</span>
                              <span className="font-medium" style={{ color: config.color }}>
                                {config.label}
                              </span>
                            </div>
                            <span className="text-sm font-semibold">
                              {((location.confidenceScore || 0) * 100).toFixed(0)}%
                            </span>
                          </div>
                          {location.label && location.label !== config.label && (
                            <div className="text-xs text-gray-600 font-medium mb-1 ml-8">
                              {location.label}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mb-2 ml-8 flex items-center gap-1">
                            <AddressDisplay
                              lat={location.centerLat}
                              lon={location.centerLon}
                              compact
                            />
                            {location.centerLat && location.centerLon && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${location.centerLat},${location.centerLon}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-blue-500 hover:text-blue-700 hover:underline ml-1"
                                title="Open in Google Maps"
                              >
                                &#x1F5FA;&#xFE0F;
                              </a>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <span className="font-medium">{location.visitCount || 0}</span> visits
                            </div>
                            <div>
                              <span className="font-medium">{formatDwellTime(location.avgDwellMinutes)}</span> avg
                            </div>
                            <div>
                              <span className="font-medium">{Math.round(location.radiusMeters || 0)}m</span> radius
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No significant locations found.</p>
                )}
              </div>
            </div>

            {/* Right Column - Map */}
            <div className="lg:col-span-2 space-y-6">
              {/* Map */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3">Location Map</h3>
                <MapContainer
                  center={getMapCenter()}
                  zoom={10}
                  scrollWheelZoom
                  style={{ height: '600px', width: '100%' }}
                  className="rounded-lg"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {polModel.significantLocations?.length > 0 && (
                    <FitBounds locations={polModel.significantLocations} />
                  )}
                  {selectedLocation && <ZoomToLocation location={selectedLocation} />}
                  {polModel.significantLocations?.map((location, idx) => {
                    const config =
                      LOCATION_TYPE_CONFIG[location.locationType] || LOCATION_TYPE_CONFIG.unknown;
                    const isSelected = selectedLocation?.locationId === location.locationId;
                    return (
                      <React.Fragment key={location.locationId || idx}>
                        {/* Geographic radius circle (actual area) */}
                        <Circle
                          center={[location.centerLat, location.centerLon]}
                          radius={location.radiusMeters || 100}
                          pathOptions={{
                            color: config.color,
                            fillColor: config.color,
                            fillOpacity: 0.15,
                            weight: 1,
                            dashArray: '5, 5',
                          }}
                        />
                        {/* Visible marker (pixel-based, always visible) */}
                        <CircleMarker
                          center={[location.centerLat, location.centerLon]}
                          radius={isSelected ? 18 : 14}
                          pathOptions={{
                            color: '#fff',
                            fillColor: config.color,
                            fillOpacity: 0.9,
                            weight: isSelected ? 4 : 3,
                          }}
                          eventHandlers={{
                            click: () => handleLocationClick(location),
                          }}
                        >
                          <Popup>
                            <div className="text-sm">
                              <div className="font-semibold flex items-center gap-1">
                                <span>{config.icon}</span>
                                <span style={{ color: config.color }}>{config.label}</span>
                              </div>
                              {location.label && location.label !== config.label && (
                                <div className="text-xs text-gray-600 font-medium">{location.label}</div>
                              )}
                              <div className="mt-1 text-gray-600">
                                <p>Visits: {location.visitCount || 0}</p>
                                <p>Avg Dwell: {formatDwellTime(location.avgDwellMinutes)}</p>
                                <p>Confidence: {((location.confidenceScore || 0) * 100).toFixed(0)}%</p>
                                <p>Radius: {Math.round(location.radiusMeters || 0)}m</p>
                              </div>
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${location.centerLat},${location.centerLon}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:underline text-xs mt-1 inline-block"
                              >
                                Open in Google Maps
                              </a>
                            </div>
                          </Popup>
                        </CircleMarker>
                      </React.Fragment>
                    );
                  })}
                </MapContainer>

                {/* Map Legend */}
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  {Object.entries(LOCATION_TYPE_CONFIG).map(([key, config]) => (
                    <div key={key} className="flex items-center gap-1">
                      <span>{config.icon}</span>
                      <span style={{ color: config.color }}>{config.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ================================================================== */}
      {/*  SECTION 2 — Expected Schedule (Routine Matrix)                    */}
      {/* ================================================================== */}
      {polModel?.routineMatrix && Object.keys(polModel.routineMatrix).length > 0 && (
        <div id="schedule" className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-lg font-semibold">Expected Schedule</h3>
            <FeatureInfoButton feature="pol" label="Docs" />
          </div>
          <div className="overflow-x-auto">
            {Object.entries(polModel.routineMatrix).map(([locationId, dayMatrix]) => {
              const location = polModel.significantLocations?.find(
                (l) => l.locationId === locationId
              );
              const config =
                LOCATION_TYPE_CONFIG[location?.locationType] || LOCATION_TYPE_CONFIG.unknown;

              return (
                <div key={locationId} className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span>{config.icon}</span>
                    <span className="font-medium" style={{ color: config.color }}>
                      {config.label}
                    </span>
                    {location?.label && location.label !== config.label && (
                      <span className="text-xs text-gray-600">{location.label}</span>
                    )}
                    {location?.centerLat && location?.centerLon && (
                      <>
                        <span className="text-xs text-gray-500">
                          &mdash; <AddressDisplay lat={location.centerLat} lon={location.centerLon} compact />
                        </span>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${location.centerLat},${location.centerLon}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:text-blue-700 hover:underline"
                          title="Open in Google Maps"
                        >
                          &#x1F5FA;&#xFE0F; Map
                        </a>
                      </>
                    )}
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="p-1 text-left"></th>
                        {TIME_PERIODS.map((period) => (
                          <th key={period} className="p-1 text-center">
                            <div>{period}</div>
                            <div className="text-gray-400 font-normal">
                              {TIME_PERIOD_LABELS[period]}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {DAYS_OF_WEEK.map((day) => (
                        <tr key={day}>
                          <td className="p-1 font-medium">{day.substring(0, 3)}</td>
                          {TIME_PERIODS.map((period) => {
                            const value = dayMatrix?.[day]?.[period];
                            return (
                              <td key={period} className="p-1">
                                <div
                                  className={`${getHeatmapColor(
                                    value
                                  )} rounded p-2 text-center`}
                                  title={`${((value || 0) * 100).toFixed(0)}%`}
                                >
                                  {value !== undefined && value !== null
                                    ? `${(value * 100).toFixed(0)}%`
                                    : '-'}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          {/* Heatmap Legend */}
          <div className="mt-4 flex items-center gap-2 text-xs">
            <span className="text-gray-500">Probability:</span>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-gray-100 rounded"></div>
              <span>0%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-green-100 rounded"></div>
              <span>1-20%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-green-200 rounded"></div>
              <span>20-40%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-green-300 rounded"></div>
              <span>40-60%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-green-400 rounded"></div>
              <span>60-80%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-green-500 rounded"></div>
              <span>80-100%</span>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/*  SECTION 3 — Boundaries (Smart Geofencing)                         */}
      {/* ================================================================== */}
      <div id="boundaries">
        {smartGeofences && (
          <div className="bg-white rounded-lg shadow p-4 space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-semibold">Boundaries</h3>
              <FeatureInfoButton feature="smartgeo" label="Docs" />
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-emerald-600">{smartGeofences.geofenceCount || 0}</div>
                <div className="text-xs text-gray-600">Total Geofences</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {smartGeofences.geofences?.filter(g => g.fence_type === 'home').length || 0}
                </div>
                <div className="text-xs text-gray-600">Home</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {smartGeofences.geofences?.filter(g => g.fence_type === 'work').length || 0}
                </div>
                <div className="text-xs text-gray-600">Work</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-amber-600">
                  {smartGeofences.geofences?.filter(g => g.fence_type === 'frequent').length || 0}
                </div>
                <div className="text-xs text-gray-600">Frequent</div>
              </div>
            </div>

            {/* Home Mismatch Warning */}
            {smartGeofences.homeMismatch?.mismatch && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                <div className="flex items-center gap-2 text-yellow-800">
                  <span className="text-xl">&#x26A0;</span>
                  <span className="font-semibold">Home Address Mismatch Detected</span>
                </div>
                <p className="text-sm text-yellow-700 mt-1">
                  Detected home location is {Math.round(smartGeofences.homeMismatch.distanceMeters || 0)}m
                  from registered address. This may indicate incorrect address on file or possible fraud.
                </p>
              </div>
            )}

            {/* Geofence List */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700">Detected Geofences</h4>
              {smartGeofences.geofences?.map((gf, index) => {
                const config = getGeofenceTypeConfig(gf.fence_type);
                return (
                  <div key={gf.geofence_id || index} className="border rounded-lg p-3" style={{ borderLeftWidth: '4px', borderLeftColor: config.color }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{config.icon}</span>
                          <span className="font-semibold">{gf.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: config.bgColor, color: config.color }}>
                            {config.label}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          <AddressDisplay lat={gf.center_lat} lon={gf.center_lon} compact />
                          <span className="mx-2">|</span>
                          <span>Radius: {Math.round(gf.radius_meters)}m</span>
                          <span className="mx-2">|</span>
                          <span>Confidence: {(gf.confidence * 100).toFixed(0)}%</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          <span>Alert On: <strong>{gf.alert_on}</strong></span>
                          <span className="mx-2">|</span>
                          <span>Active Hours: {formatActiveHours(gf.active_hours)}</span>
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs ${gf.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {gf.is_active ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Map with Geofences */}
            {smartGeofences.geofences?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-gray-700 mb-2">Geofence Map</h4>
                <div className="border rounded-lg overflow-hidden" style={{ height: '400px' }}>
                  <MapContainer
                    center={[
                      smartGeofences.geofences[0]?.center_lat || 33.4484,
                      smartGeofences.geofences[0]?.center_lon || -112.074
                    ]}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {smartGeofences.geofences.map((gf, index) => {
                      const config = getGeofenceTypeConfig(gf.fence_type);
                      return (
                        <React.Fragment key={gf.geofence_id || index}>
                          <Circle
                            center={[gf.center_lat, gf.center_lon]}
                            radius={gf.radius_meters}
                            pathOptions={{
                              color: config.color,
                              fillColor: config.color,
                              fillOpacity: 0.2,
                              weight: 2,
                            }}
                          >
                            <Popup>
                              <div className="text-sm">
                                <strong>{gf.name}</strong>
                                <br />
                                Type: {config.label}
                                <br />
                                Radius: {Math.round(gf.radius_meters)}m
                                <br />
                                Confidence: {(gf.confidence * 100).toFixed(0)}%
                              </div>
                            </Popup>
                          </Circle>
                          <CircleMarker
                            center={[gf.center_lat, gf.center_lon]}
                            radius={6}
                            pathOptions={{
                              color: config.color,
                              fillColor: config.color,
                              fillOpacity: 1,
                            }}
                          />
                        </React.Fragment>
                      );
                    })}
                  </MapContainer>
                </div>
              </div>
            )}

            {/* Smart Geofencing Reference (collapsible) */}
            <details className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <summary className="font-semibold text-emerald-800 cursor-pointer">Smart Geofencing Reference</summary>
              <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                <div>
                  <h5 className="font-medium text-emerald-700 mb-2">Location Types</h5>
                  <ul className="space-y-1 text-gray-700">
                    <li><span className="text-lg">{'\uD83C\uDFE0'}</span> <strong>Home:</strong> Highest night presence, {'>'} 4 nights</li>
                    <li><span className="text-lg">{'\uD83C\uDFE2'}</span> <strong>Work:</strong> Weekday daytime, {'>'} 3 weekdays</li>
                    <li><span className="text-lg">{'\uD83D\uDCCD'}</span> <strong>Frequent:</strong> 10+ visits, 30+ min dwell</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-emerald-700 mb-2">Radius Adjustments</h5>
                  <ul className="space-y-1 text-gray-700">
                    <li><strong>Home:</strong> Detected radius x 1.2 (min 100m)</li>
                    <li><strong>Work:</strong> Detected radius x 1.5 (min 150m)</li>
                    <li><strong>Frequent:</strong> Detected radius x 1.3 (min 100m)</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-emerald-700 mb-2">Default Active Hours</h5>
                  <ul className="space-y-1 text-gray-700">
                    <li><strong>Home:</strong> 6pm - 8am (every day)</li>
                    <li><strong>Work:</strong> 8am - 6pm (weekdays)</li>
                    <li><strong>Frequent:</strong> Always active</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-emerald-700 mb-2">Alert Settings</h5>
                  <ul className="space-y-1 text-gray-700">
                    <li><strong>Home:</strong> Alert on exit (unexpected departure)</li>
                    <li><strong>Work:</strong> Informational only</li>
                    <li><strong>Restricted:</strong> Alert on entry</li>
                  </ul>
                </div>
              </div>
            </details>
          </div>
        )}
      </div>

      {/* ================================================================== */}
      {/*  SECTION 4 — Location Check (Unified Deviation + Compliance)      */}
      {/* ================================================================== */}
      <div id="location-check">
        <div className="bg-white rounded-lg shadow p-4 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Location Check</h3>
            <FeatureInfoButton feature="pol" label="Docs" />
          </div>
          <p className="text-sm text-gray-600">
            Checks the defendant's location against their learned routine (<strong>Routine Deviation</strong>) and whether they're inside the correct geofence boundary (<strong>Geofence Compliance</strong>).
          </p>
          {/* Primary action: check defendant's latest GPS location */}
          <div className="flex flex-wrap gap-4 items-center">
            <button
              onClick={() => handleCheckLocation()}
              disabled={!personId || isCheckingLocation}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              {isCheckingLocation ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Checking...
                </span>
              ) : (
                checkLat || checkLon ? 'Check Location' : 'Check Latest GPS Location'
              )}
            </button>
            <span className="text-sm text-gray-500">
              {checkLat || checkLon
                ? 'Using manual coordinates below'
                : "Uses the defendant's most recent GPS ping"}
            </span>
          </div>

          {/* Optional: manual coordinate override */}
          <details className="text-sm">
            <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
              Or enter coordinates manually...
            </summary>
            <div className="flex flex-wrap gap-4 items-end mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={checkLat}
                  onChange={(e) => setCheckLat(e.target.value)}
                  placeholder="e.g. 33.4484"
                  className="w-40 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={checkLon}
                  onChange={(e) => setCheckLon(e.target.value)}
                  placeholder="e.g. -112.074"
                  className="w-40 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              {(checkLat || checkLon) && (
                <button
                  onClick={() => { setCheckLat(''); setCheckLon(''); }}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-red-600"
                >
                  Clear
                </button>
              )}
            </div>
          </details>

          {/* Address Preview */}
          {checkLat && checkLon && (
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <span className="text-gray-400">Address:</span>
              <AddressDisplay lat={parseFloat(checkLat)} lon={parseFloat(checkLon)} compact className="font-medium" />
            </div>
          )}

          {/* Results */}
          {(deviation || complianceResult) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
              {/* Deviation Result */}
              {deviation && (
                <div
                  className={`p-4 rounded-lg border ${
                    ALERT_LEVEL_CONFIG[deviation.alertLevel]?.bgColor || 'bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-sm">Routine Deviation</span>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                        ALERT_LEVEL_CONFIG[deviation.alertLevel]?.bgColor
                      } ${ALERT_LEVEL_CONFIG[deviation.alertLevel]?.textColor}`}
                    >
                      {ALERT_LEVEL_CONFIG[deviation.alertLevel]?.label || deviation.alertLevel}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deviation Score</span>
                      <span className="font-semibold">
                        {((deviation.deviationScore || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expected</span>
                      <span className="font-medium">{deviation.expectedLocation || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Detected As</span>
                      <span className="font-medium">{deviation.currentLocationType || 'Unknown'}</span>
                    </div>
                    {deviation.locationChecked && (
                      <div className="mt-2 pt-2 border-t border-gray-200 text-xs">
                        <span className="text-gray-500">Checked:</span>{' '}
                        <AddressDisplay
                          lat={deviation.locationChecked.latitude}
                          lon={deviation.locationChecked.longitude}
                          className="font-medium"
                          compact
                        />
                        {deviation.locationChecked.usedLatestCheckin && (
                          <span className="ml-1 text-blue-600">(latest check-in)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Compliance Result */}
              {complianceResult && (
                <div className="p-4 rounded-lg border" style={{ backgroundColor: getComplianceStatusConfig(complianceResult.compliance_status).bgColor }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-sm">Geofence Compliance</span>
                    <span className={`flex items-center gap-1 text-sm font-semibold ${complianceResult.is_compliant ? 'text-green-700' : 'text-red-700'}`}>
                      {complianceResult.is_compliant ? '\u2714' : '\u2718'}{' '}
                      {getComplianceStatusConfig(complianceResult.compliance_status).label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{complianceResult.details}</p>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expected Geofence</span>
                      <span className="font-medium">{complianceResult.expected_geofence || 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Actual Geofence</span>
                      <span className="font-medium">{complianceResult.actual_geofence || 'None'}</span>
                    </div>
                    {complianceResult.distance_from_expected_meters != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Distance from Expected</span>
                        <span className="font-medium">{Math.round(complianceResult.distance_from_expected_meters)}m</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolLocationTab;
